import React, { useState, useCallback, useRef, useEffect, useImperativeHandle } from 'react';
import type { ReactNode } from 'react';
import { PlusOutlined, GroupOutlined, ShoppingOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Spin, Tag, Typography, Popover, Empty } from 'antd';
import type { ButtonProps, InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface UnifiedItemSearchProduct {
  id: string;
  sku: string;
  name: string;
}

export interface UnifiedItemSearchBundle {
  id: string;
  sku: string;
  name: string;
  bundle_products?: unknown[] | null;
}

interface SearchListParams {
  search: string;
  limit: number;
  merchant_id?: string;
}

// Service contracts use method syntax (bivariant) + Promise<unknown> so callers passing
// looser-typed services (e.g. fetchList that returns Promise<unknown>) remain assignable.
interface ProductSearchService {
  fetchList(params: SearchListParams): Promise<unknown>;
}

interface BundleSearchService {
  fetchList(params: SearchListParams): Promise<unknown>;
}

export interface UnifiedItemSearchHandle {
  focus: () => void;
}

interface UnifiedItemSearchProps {
  productService: ProductSearchService;
  // Caller (ProductItemCard) keeps bundleService loosely-typed (`unknown`); we accept
  // the wider type and rely on a runtime truthiness check before invoking fetchList.
  bundleService?: unknown;
  merchantId?: string | null;
  // Method syntax for bivariance — callers may type their callbacks against a narrower
  // domain `Bundle` / `Product` type than what we declare here.
  onSelectProduct(product: UnifiedItemSearchProduct): void;
  onSelectBundle(bundle: UnifiedItemSearchBundle): void;
  buttonType?: ButtonProps['type'];
  buttonSize?: ButtonProps['size'];
  block?: boolean;
  buttonText?: ReactNode;
  inline?: boolean;
  disabled?: boolean;
}

interface FetchListResult<T> {
  data?: T[];
}

/**
 * 統一搜尋元件 - 同時搜尋商品與組合商品
 *
 * 兩種模式：
 * - Popover 模式（預設）：按鈕觸發浮動搜尋面板
 * - Inline 模式（inline=true）：滿版搜尋列，固定在 order_items 下方
 */
const UnifiedItemSearch = React.forwardRef<UnifiedItemSearchHandle, UnifiedItemSearchProps>(({
  productService,
  bundleService,
  merchantId,
  onSelectProduct,
  onSelectBundle,
  buttonType = 'dashed',
  buttonSize,
  block = false,
  buttonText,
  inline = false,
  disabled = false,
}, ref) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [products, setProducts] = useState<UnifiedItemSearchProduct[]>([]);
  const [bundles, setBundles] = useState<UnifiedItemSearchBundle[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<InputRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const MIN_SEARCH_LENGTH = 2;

  // 讓父層可以 scrollIntoView + focus
  useImperativeHandle(ref, () => ({
    focus: () => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => inputRef.current?.focus(), 300);
    },
  }));

  // 清除搜尋狀態
  const resetSearch = useCallback(() => {
    setSearchText('');
    setProducts([]);
    setBundles([]);
    setLoading(false);
  }, []);

  // 並行搜尋商品和組合商品
  const performSearch = useCallback(async (text: string) => {
    if (!text || text.length < MIN_SEARCH_LENGTH) {
      setProducts([]);
      setBundles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params: SearchListParams = { search: text, limit: 10 };
      if (merchantId) params.merchant_id = merchantId;

      const [productResult, bundleResult] = await Promise.all([
        productService.fetchList(params) as Promise<FetchListResult<UnifiedItemSearchProduct> | null | undefined>,
        bundleService
          ? ((bundleService as BundleSearchService).fetchList(params) as Promise<FetchListResult<UnifiedItemSearchBundle> | null | undefined>)
          : Promise.resolve({ data: [] as UnifiedItemSearchBundle[] }),
      ]);

      setProducts(productResult?.data || []);
      setBundles(bundleResult?.data || []);
    } catch (error) {
      console.error('[UnifiedItemSearch] Search failed:', error);
      setProducts([]);
      setBundles([]);
    } finally {
      setLoading(false);
    }
  }, [productService, bundleService, merchantId]);

  // debounced search
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!text || text.length < MIN_SEARCH_LENGTH) {
      setProducts([]);
      setBundles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      performSearch(text);
    }, 400);
  }, [performSearch]);

  // 清理 debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Popover 模式：開啟時聚焦
  useEffect(() => {
    if (!inline && open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else if (!inline && !open) {
      resetSearch();
    }
  }, [open, inline, resetSearch]);

  const handleSelectProduct = (product: UnifiedItemSearchProduct) => {
    onSelectProduct(product);
    if (inline) {
      resetSearch();
    } else {
      setOpen(false);
    }
  };

  const handleSelectBundle = (bundle: UnifiedItemSearchBundle) => {
    onSelectBundle(bundle);
    if (inline) {
      resetSearch();
    } else {
      setOpen(false);
    }
  };

  const hasResults = products.length > 0 || bundles.length > 0;
  const hasSearched = searchText.length >= MIN_SEARCH_LENGTH && !loading;
  const showResults = loading || hasResults || (hasSearched && !hasResults);

  // ── 共用搜尋結果 ──
  const resultsContent = (
    <>
      {loading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
        </div>
      )}

      {!loading && hasSearched && !hasResults && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('shared.orders.form.unified_search.no_results')}
        />
      )}

      {/* 商品區塊 */}
      {products.length > 0 && (
        <>
          <div style={{ padding: '4px 8px', background: '#fafafa', fontWeight: 600, fontSize: 12, color: '#666' }}>
            <ShoppingOutlined style={{ marginRight: 4 }} />
            {t('shared.orders.form.unified_search.section_products')}
          </div>
          {products.map((product) => (
            <div
              key={product.id}
              data-testid={`order-item-result-${product.sku}`}
              onClick={() => handleSelectProduct(product)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e6f4ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Text>{product.sku}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>{product.name}</Text>
            </div>
          ))}
        </>
      )}

      {/* 組合商品區塊 */}
      {bundles.length > 0 && (
        <>
          <div style={{ padding: '4px 8px', background: '#fafafa', fontWeight: 600, fontSize: 12, color: '#666', marginTop: products.length > 0 ? 8 : 0 }}>
            <GroupOutlined style={{ marginRight: 4 }} />
            {t('shared.orders.form.unified_search.section_bundles')}
          </div>
          {bundles.map((bundle) => (
            <div
              key={bundle.id}
              onClick={() => handleSelectBundle(bundle)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e6f4ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Tag icon={<GroupOutlined />} color="blue" style={{ marginRight: 8 }}>
                {t('shared.orders.form.bundle.tag')}
              </Tag>
              <Text>{bundle.sku}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>{bundle.name}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({bundle.bundle_products?.length || 0} {t('shared.orders.form.unified_search.items')})
              </Text>
            </div>
          ))}
        </>
      )}
    </>
  );

  // ══════ Inline 模式：滿版搜尋列 ══════
  if (inline) {
    return (
      <div ref={containerRef} style={{ marginTop: 16, borderTop: '1px dashed #d9d9d9', paddingTop: 16 }}>
        <Input
          ref={inputRef}
          data-testid="order-item-search-input"
          placeholder={t('shared.orders.form.unified_search.placeholder')}
          value={searchText}
          onChange={handleSearchChange}
          allowClear
          disabled={disabled}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          size="large"
        />
        {showResults && (
          <div style={{
            marginTop: 8,
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            maxHeight: 400,
            overflow: 'auto',
          }}>
            {resultsContent}
          </div>
        )}
      </div>
    );
  }

  // ══════ Popover 模式（預設）══════
  const popoverContent = (
    <div style={{ width: 480 }}>
      <Input
        ref={inputRef}
        data-testid="order-item-search-input"
        placeholder={t('shared.orders.form.unified_search.placeholder')}
        value={searchText}
        onChange={handleSearchChange}
        allowClear
        style={{ marginBottom: 12 }}
      />

      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        {!loading && searchText.length < MIN_SEARCH_LENGTH && (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
            {t('shared.orders.form.unified_search.hint', { min: MIN_SEARCH_LENGTH })}
          </div>
        )}
        {resultsContent}
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      // disabled 時不接受 click 開啟 popover；用 controlled open 強制關閉
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
      placement="topLeft"
      arrow={false}
    >
      <Button
        data-testid="order-add-item-btn"
        type={buttonType}
        size={buttonSize}
        icon={<PlusOutlined />}
        block={block}
        disabled={disabled}
      >
        {buttonText || t('shared.orders.form.add_item')}
      </Button>
    </Popover>
  );
});

export default UnifiedItemSearch;
